import { useId, type FC } from 'react';
import { Copy, Layers } from 'lucide-react';
import { MultiSearchableSelect } from '@/components/ui/form/input/multi-searchable-select';
import { useSenderPromptGroups } from '@/features/campaign-texts/hooks/useSenderPromptGroups';
import { getActiveSelectableLeafGroups, optionLabelForGroup } from '@/features/campaign-texts/utils/group-tree.utils';
import { cn } from '@/lib/utils';

interface Props {
  groupIds: string[];
  amount: number;
  mode?: 'SHARED' | 'UNIQUE';
  onGroups: (ids: string[]) => void;
  onAmount: (amount: number) => void;
  onMode: (mode: 'SHARED' | 'UNIQUE') => void;
}
const LABEL = 'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

/** Group, amount and (for automatic AI) who the names apply to, on one row. */
export const SenderAiFields: FC<Props> = ({ groupIds, amount, mode, onGroups, onAmount, onMode }) => {
  const amountId = useId();
  const groups = useSenderPromptGroups();
  const all = groups.data ?? [];
  const options = getActiveSelectableLeafGroups(all).map((g) => ({ value: g.id, label: optionLabelForGroup(g, all) }));
  const names = groupIds.map((id) => options.find((o) => o.value === id)?.label ?? id).join(' → ');
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-56 flex-1 space-y-1.5">
        <p className={LABEL}>Group</p>
        <MultiSearchableSelect options={options} value={groupIds} onChange={onGroups} placeholder="Choose groups…" summaryNoun="groups" searchPlaceholder="Search groups…" />
      </div>
      <div className="space-y-1.5">
        <label htmlFor={amountId} className={LABEL}>Amount</label>
        <select id={amountId} value={amount} onChange={(e) => onAmount(Number(e.target.value))} className="h-10 w-20 rounded-md border border-border bg-background px-2 text-sm">
          {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      {mode ? <ApplyTo mode={mode} onMode={onMode} /> : null}
      {groupIds.length > 1 ? <p className="basis-full text-xs text-muted-foreground">{mode ? 'Groups rotate across drops' : 'Generate from'}: {names}</p> : null}
    </div>
  );
};

const ApplyTo: FC<{ mode: 'SHARED' | 'UNIQUE'; onMode: (mode: 'SHARED' | 'UNIQUE') => void }> = ({ mode, onMode }) => (
  <div className="space-y-1.5">
    <p className={LABEL}>Apply to</p>
    <div className="flex gap-1 rounded-lg border border-border bg-muted/20 p-1" role="group" aria-label="Apply to">
      {([{ value: 'SHARED', label: 'Same for all', Icon: Copy }, { value: 'UNIQUE', label: 'Per drop', Icon: Layers }] as const).map(({ value, label, Icon }) => (
        <button key={value} type="button" aria-pressed={mode === value} onClick={() => onMode(value)} className={cn('flex h-8 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium', mode === value ? 'bg-background text-foreground shadow-sm ring-2 ring-primary' : 'text-muted-foreground hover:text-foreground')}>
          <Icon className="h-3.5 w-3.5" />{label}
        </button>
      ))}
    </div>
  </div>
);
